import type { FlowRole } from '../role';
// Type-only: view.ts imports this folder at runtime, so a value import would cycle.
import type { SavedViewCreateBody } from '../view';
import { FLOW_ACCOUNT_TABS } from '../flow-account-tabs';
import { GS_ACCOUNT_TABS } from '../gs-account-tabs';
import { GS_LEDGER_COLUMN_LABELS } from '../gs-ledger-columns';
import type {
  FlowAccountLayoutId,
  FlowAccountLayoutSpec,
  FlowHomeLayoutId,
  FlowHomeLayoutSpec,
  FlowLayoutTable,
  FlowListLayoutId,
  FlowListLayoutSpec,
  UiViewFlowLayouts,
} from './schema';

/**
 * The layout registry: every layout each surface offers, in menu order.
 *
 * ## Adding a list layout for one table
 *
 * 1. Add its id to `FlowListLayoutIdSchema` in `schema.ts`.
 * 2. Add its entry to `LIST_LAYOUTS` below, with `appliesTo` naming only the
 *    table(s) it skins and its per-table maps (`columnLabels`,
 *    `defaultWidths`, `cells`, `columnAlign`, `rowDensity`) keyed by those tables only.
 * 3. Optionally set `fixedColumns` (per table: those keys render first, in
 *    that order, then the rep's own columns; the saved column list is never
 *    rewritten).
 * 4. Optionally make it a UI View's default for that table via
 *    `UI_VIEW_FLOW_LAYOUTS[role].listByTable`, or the table's own "Flow default"
 *    via `FLOW_LAYOUT_TABLE_FALLBACK_ID` (a complete per-table record, so a new
 *    table must name its default).
 *
 * `columnLabels`, `defaultWidths`, `cells`, `columnAlign` and `rowDensity` are
 * all per-table maps (absent table = no change; absent density = dense). The
 * resolver (`resolve.ts`) is generic over the id and reads nothing from here:
 * hooks pass it the role default and the surface's fallback.
 *
 * Nothing else changes: the layout menu lists `listFlowLayoutsFor(table)` and
 * the resolver skips any candidate the table does not offer, so no menu or
 * resolver edit is needed.
 */

/** The final fallback for the account and Home surfaces, badged "Flow default". */
export const FLOW_LAYOUT_FALLBACK_ID = 'cdr' as const;

/** Per-table "Flow default" for the list surface: the final fallback, badged "Flow default". */
export const FLOW_LAYOUT_TABLE_FALLBACK_ID: Record<FlowLayoutTable, FlowListLayoutId> = {
  companies: 'cdr',
  people: 'cdr',
  plays: 'cdr',
  signals: 'signal-response',
};

/**
 * The default column widths of the Growth Strategist ledger, keyed by Flow's
 * account field key.
 *
 * These are the base widths of the GS design (egs-view at e7603325b) (`GS_LEDGER_BASE_COLUMN_WIDTHS`
 * in its `gsLedgerColumns.tsx`, renamed to Flow's keys), with Account
 * widened. Account is 320, measured: a 44-character "Segment · Industry"
 * line measured 247px in the live font, plus the avatar, the gap and the
 * cell padding, which the design's 260 did not fit. Account stays the widest
 * column.
 *
 * They are only defaults: a width the user dragged wins over the one here.
 */
export const GS_DEFAULT_COLUMN_WIDTHS: Readonly<Record<string, number>> = {
  name: 320,
  health: 140,
  credit_pace: 170,
  credits_l30: 150,
  contract_end: 150,
  arr: 130,
  renewal_date: 150,
  gs_name: 180,
  red_reason: 220,
  risk_reasons: 240,
  company_segment: 160,
  industry: 160,
  sales_tier: 140,
  region: 140,
  next_gs: 160,
  next_gs_assign_date: 150,
  claydr_name: 160,
  last_gs_meeting: 160,
  last_claydr_meeting: 180,
  last_gtme_meeting: 170,
};

const CDR_NAME = 'Prospecting';
const CDR_AUDIENCE = '(CDR/GTME)';
const GS_NAME = 'Health';
const GS_AUDIENCE = '(Post-Sales)';

export const LIST_LAYOUTS = {
  cdr: {
    id: 'cdr',
    name: CDR_NAME,
    audience: CDR_AUDIENCE,
    appliesTo: ['companies', 'people', 'plays', 'signals'],
    columnLabels: {},
    defaultWidths: {},
    cells: {},
    columnAlign: {},
    alignAllButFirst: null,
    showRowNumbers: true,
    rowDensity: {},
    showTracker: true,
  },
  gs: {
    id: 'gs',
    name: GS_NAME,
    audience: GS_AUDIENCE,
    appliesTo: ['companies', 'plays'],
    columnLabels: { companies: GS_LEDGER_COLUMN_LABELS },
    defaultWidths: { companies: GS_DEFAULT_COLUMN_WIDTHS },
    cells: { companies: { name: 'gs-account', credit_pace: 'pace-stacked' } },
    columnAlign: {},
    alignAllButFirst: 'center',
    showRowNumbers: false,
    rowDensity: { companies: 'tall' },
    showTracker: false,
  },
  'signal-response': {
    id: 'signal-response',
    name: 'Signal Response',
    audience: CDR_AUDIENCE,
    appliesTo: ['signals'],
    columnLabels: {
      signals: { signal_source_type: 'Signal', account_owner_name: 'Owners' },
    },
    defaultWidths: {
      signals: {
        // Spencer 10/21: Signal, Account and Local time were far too wide (empty space on the right), so
        // they are cut toward their real content; Days on step takes the narrow-number width (at most ~"999d").
        signal_source_type: 220,
        step_since: 126,
        contact_name: 276,
        account_name: 240,
        local_time: 150,
        account_owner_name: 236,
        campaign_name: 236,
        event_date: 186,
        // The extras a rep may add under the fixed core. The layout has no per-table
        // fallback width, so the common ones are listed: each is
        // wide so the room reads as space between columns, not cell padding.
        contact_title: 236,
        flow_name: 236,
        step_number: 126,
        signal_tactic: 236,
        contact_location: 236,
        due_date: 186,
        signal_status: 186,
        contact_linkedin: 176,
      },
    },
    cells: {
      signals: {
        signal_source_type: 'signal-stacked',
        step_since: 'step-since',
        contact_name: 'contact-stacked',
        account_name: 'account-stacked',
        local_time: 'local-time-stacked',
        account_owner_name: 'owners-stacked',
        flow_name: 'flow-stacked',
      },
    },
    columnStacks: {
      signals: {
        signal_source_type: [{ key: 'signal_source_type', short: 'Type' }, { key: 'signal_source_subtype', short: 'Subtype' }],
        step_since: [{ key: 'step_since', short: 'Days on step' }],
        contact_name: [{ key: 'contact_name', short: 'Name' }, { key: 'contact_title', short: 'Title' }],
        account_name: [{ key: 'account_name', short: 'Name' }, { key: 'account_type', short: 'Type' }, { key: 'account_sales_tier', short: 'Tier' }],
        local_time: [{ key: 'business_hours', short: 'Hours' }, { key: 'contact_mailing_city', short: 'City' }],
        account_owner_name: [{ key: 'account_claydr_name', short: 'ClayDR' }, { key: 'account_owner_name', short: 'Owner' }],
        flow_name: [{ key: 'flow_name', short: 'Name' }, { key: 'step_number', short: 'Step' }, { key: 'signal_tactic', short: 'Step type' }],
      },
    },
    columnAlign: {},
    alignAllButFirst: null,
    showRowNumbers: false,
    rowDensity: { signals: 'roomy' },
    showTracker: false,
    fixedColumns: {
      signals: [
        'signal_source_type',
        'step_since',
        'contact_name',
        'account_name',
        'local_time',
        'account_owner_name',
        'flow_name',
        'campaign_name',
        'event_date',
      ],
    },
  },
} as const satisfies Record<FlowListLayoutId, FlowListLayoutSpec>;

export const ACCOUNT_LAYOUTS = {
  cdr: {
    id: 'cdr',
    name: CDR_NAME,
    audience: CDR_AUDIENCE,
    tabs: FLOW_ACCOUNT_TABS,
    defaultTab: 'timeline',
  },
  gs: {
    id: 'gs',
    name: GS_NAME,
    audience: GS_AUDIENCE,
    tabs: GS_ACCOUNT_TABS,
    defaultTab: 'summary',
  },
} as const satisfies Record<FlowAccountLayoutId, FlowAccountLayoutSpec>;

export const HOME_LAYOUTS = {
  cdr: {
    id: 'cdr',
    name: CDR_NAME,
    audience: CDR_AUDIENCE,
    columns: ['cold_accounts', 'open_loops', 'todays_meetings'],
    cardFacts: [],
    showDealBrief: true,
    loopLink: 'open-loops-page',
  },
  gs: {
    id: 'gs',
    name: GS_NAME,
    audience: GS_AUDIENCE,
    columns: ['open_loops', 'todays_meetings'],
    cardFacts: ['health', 'pace', 'contract-end'],
    showDealBrief: false,
    loopLink: 'account-open-loops',
  },
} as const satisfies Record<FlowHomeLayoutId, FlowHomeLayoutSpec>;

/**
 * Each UI View's default layout per surface. Defaults only: a rep's own
 * choice always wins. View labels come from `teamLabel()` in `role-view.ts`.
 */
export const UI_VIEW_FLOW_LAYOUTS: Record<FlowRole, UiViewFlowLayouts> = {
  claydr: { defaults: { list: 'cdr', account: 'cdr', home: 'cdr' }, listByTable: { signals: 'signal-response' } },
  gtme: { defaults: { list: 'cdr', account: 'cdr', home: 'cdr' }, listByTable: { signals: 'signal-response' } },
  gs: { defaults: { list: 'gs', account: 'gs', home: 'gs' } },
};

/** The list layouts a table offers, in registry (menu) order. */
export function listFlowLayoutsFor(table: FlowLayoutTable): FlowListLayoutSpec[] {
  return Object.values<FlowListLayoutSpec>(LIST_LAYOUTS).filter(spec => spec.appliesTo.includes(table));
}

/** The layout table a saved-view entity's list renders in. */
export function layoutTableForViewEntity(entity: SavedViewCreateBody['entity']): FlowLayoutTable {
  switch (entity) {
    case 'account': return 'companies';
    case 'contact': return 'people';
    case 'signal': return 'signals';
    case 'play': return 'plays';
  }
}

/** Whether `table` offers the layout `id` (a pin for a layout the table lacks is invalid). */
export function flowLayoutOffered(table: FlowLayoutTable, id: string): boolean {
  return listFlowLayoutsFor(table).some(spec => spec.id === id);
}
